import { useCallback, useEffect, useState } from "react";
import { type DirectoryTree } from "@/api";
import { createLogger } from "@/logging";
import { resolveLoadErrorMessage, type LoadState } from "../loadState";
import type { FileBrowserSource } from "../fileBrowserSource";

const logger = createLogger("components/dataset/fileBrowser");

export type UseDirectoryTreeResult = {
  state: LoadState<DirectoryTree>;
  // 에러 시 재시도(현재 리소스 트리를 다시 조회한다).
  reload: () => void;
};

// 데이터셋의 canonical+extra 병합 디렉터리 트리(Files 모드 좌측). 폴더 스켈레톤이므로
// 한 번만 조회한다. 폴더를 펼칠 때의 목록(에피소드·직속 파일)은 노드별로 따로 조회한다.
export function useDirectoryTree(
  source: FileBrowserSource,
  datasetUuid: string,
): UseDirectoryTreeResult {
  const [state, setState] = useState<LoadState<DirectoryTree>>({ status: "idle" });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!datasetUuid) {
      setState({ status: "idle" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    void source
      .directoryTree(datasetUuid)
      .then((data) => {
        if (!cancelled) setState({ status: "success", data });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        logger.error("directoryTree failed", error);
        setState({ status: "error", message: resolveLoadErrorMessage(error) });
      });
    return () => {
      cancelled = true;
    };
  }, [source, datasetUuid, reloadToken]);

  const reload = useCallback(() => setReloadToken((current) => current + 1), []);
  return { state, reload };
}
